import React, { useEffect, useState } from 'react';
import SubmitButton from './SubmitButton';
import { errorText, JFROG_API_SRP_JSON_REPO_URL, READINESS_CHECK_TOOLTIP } from '../../../../utilities/constants';
import { buildSchemaErrorReadoutUI, schemaValidation } from '../../../../utilities/schemaValidation';
import { StyledValidateButton } from '../../../styledComponents/Buttons.styles';
import palette from '../../../../utilities/palette';
import SubmitModal from './SubmitModal';
import { getJiraAPIBaseHost, sanitizeUrl } from '../../../../utilities/utilityFunctions';
import { useDataContext } from '../../../DataProvider/DataContext';
import { reorderClusterListBySwInstallationSequence } from '../../../../utilities/ecuCatalogeUtils';
import { statusTemplates } from './SubmitSectionHelper';

const SubmitSection = (
    {
        jsonFunction, 
        isFormValid, 
        dryRun,
        handleComplete,
        isResetButtonClicked,
        setIsResetButtonClicked
    }) => {

    const { schema, api, architectureType, signingAllowedModules } = useDataContext();


    // Handle submit processing states
    const [submitStatus, setSubmitStatus] = useState("");
    const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
    const [isSubmitFailure, setIsSubmitFailure] = useState(false);
    const [isProcessingSubmit, setIsProcessingSubmit] = useState(false);
    const [modalType, setModalType] = useState(null);

    // object with status readouts for each call
    const [statusDisplay, setStatusDisplay] = useState({});
    
    // for displaying links
    const [issueLink, setIssueLink] = useState(null);
    const [pathToPublishedSRPd, setPathToPublishedSRPd] = useState(null);

    // schema errors
    const [schemaErrorReadout, setSchemaErrorReadout] = useState([]);


    useEffect(() => {
        console.log("Schema updated in SubmitSection: ", schema);
    }, [schema]);


    useEffect(() => {
        if (isResetButtonClicked) {
            console.log("Reset button clicked - resetting submit section states");
            setIsSubmitSuccess(false);
            setIsSubmitFailure(false);
            setIsProcessingSubmit(false);
            setSubmitStatus("");
            setStatusDisplay({});
            setIssueLink(null);
            setPathToPublishedSRPd(null);
            setSchemaErrorReadout([]);
            setIsResetButtonClicked(false);
        }
    }, [isResetButtonClicked]);


    function handleJsonFunctionError(errorCode: any, context: 'submit' | 'validate') {
        if (errorCode === null || errorCode === undefined) {
            return false;
        }

        switch(errorCode){
            case("notJson"):
                setSubmitStatus(errorText.fileNotJSON);
                return true;
            case("failedParse"):
                setSubmitStatus(errorText.parsingError);
                return true;
            case("requiredFieldMissing"):
                if (context === 'submit') {
                    setSubmitStatus(errorText.requiredFieldMissing);
                    return true;
                }
                return false;
            case("failedBuild"):
                if (context === 'submit') {
                    setSubmitStatus(errorText.buildingError);
                    return true;
                }
                return false;
            case("outdatedSchema"):
                setSubmitStatus(errorText.schemaOutOfDate);
                setIsSubmitFailure(true);
                return true;
            default:
                return false;
        }
    }

    function validateFormAndSchema({
        srpContent,
        onSchemaValidationFail,
    }: {
        srpContent: any,
        onSchemaValidationFail?: () => void,
    }) {
        const validation = schemaValidation(schema, srpContent, architectureType, signingAllowedModules);
        let isValid = true;

        if (!isFormValid) {
            isValid = false;
        }

        const errorsReadout = buildSchemaErrorReadoutUI(validation.errors ?? []);
        setSchemaErrorReadout(errorsReadout);
        if (!validation.isValid && validation.errors.length !== 0) {
            onSchemaValidationFail?.();
            isValid = false;
        }

        return isValid;
    }




    const handleConfirmation = async (e?) => {

        e.preventDefault();

        // Don't allow multiple submissions without refresh
        if (isSubmitSuccess) {
            setSubmitStatus(errorText.alreadyCreated);
            return;
        }

        // Don't allow for submission while already submitting
        if (isProcessingSubmit) {
            setSubmitStatus(errorText.alreadyInProgress);
            return;
        }


        setSchemaErrorReadout([]);

        // Fetch JSON content from the passed function
            // Expects 'error' in object if there was an error grabbing the JSON content
        const data = await jsonFunction();

        //set the status to an appropriate message if there was an error in getting the JSON content
        if (handleJsonFunctionError(data.error, 'submit')) {
            setIsProcessingSubmit(false);
            return;
        }

        setSubmitStatus("");

        const {srpContent, _} = data;


        handleComplete();

        const isValid = validateFormAndSchema({
            srpContent,
            onSchemaValidationFail: () => setIsProcessingSubmit(false),
        });

        if (!isValid) {
            return;
        }

        validationSuccessful();
        setStatusDisplay({});
        setIsSubmitFailure(false);
        setIssueLink(null);
        setPathToPublishedSRPd(null);
        setModalType('confirm');
        setIsProcessingSubmit(true);
    }

    // Overall submit process function
    const handleSubmit = async (e) => {
        try {
        e.preventDefault();

        const data = await jsonFunction();
        
        const {srpContent, extraSRPRelatedContent} = data;

        console.log("EXTRA SRP RELATED CONTENT: ", extraSRPRelatedContent);

        srpContent.srpdVersion = srpContent.srpdVersion ?? "1.0.0";

        const fileName = `${srpContent.srpdName}_${srpContent.srpdVersion}SRPd`

        // Need independant tracking variable since statusDisplay won't update fast enough
        let statusTracker = {};

        // Reorder clusterList by swInstallationSequence
        reorderClusterListBySwInstallationSequence(srpContent);

        // Submit SRPd
        let issueKey = "";
        try {
            statusTracker = {
                srpdSubmission: dryRun ? statusTemplates.srpdValidationStart : statusTemplates.srpdSubmissionStart
            }
            setStatusDisplay(statusTracker);

            const submissionBody = {
                srpd: srpContent,
                extras: extraSRPRelatedContent
            };
            const headers: Record<string, string> = {
                'Content-Type': 'application/json'
            };
            if (!dryRun) {
                headers['CR-Tenant-Name'] = 'fnv';
            }

            const response = await api.fetch(dryRun ? '/entrypoint/srps/validate' : '/entrypoint/srps', {
                method: 'POST',
                headers,
                body: JSON.stringify(dryRun ? srpContent : submissionBody),
            }, 'v4');
            if (!response.ok) {
                console.warn("Error when submitting SRPd: ",response.status, response.statusText);

                const json = await response.json().catch(() => null);
                const validationErrors = dryRun && Array.isArray(json?.error?.errors)
                    ? json.error.errors.filter((detail: unknown) => typeof detail === 'string')
                    : [];
                const errorMessage = validationErrors.length > 0 ? validationErrors.join('\n')
                    : typeof json?.message === 'string' ? json.message
                    : typeof json?.error === 'string' ? json.error
                    : `${dryRun ? 'SRPd validation' : 'SRP submission'} failed (HTTP ${response.status})`;
                console.error("Error message from server: ", errorMessage);
                throw new Error(`${errorMessage}`);
            }

            const srpdSubmissionJson = await response.json();

            if (!dryRun) {
                issueKey = srpdSubmissionJson.issueKey;
            }

            statusTracker = {
                ...statusTracker, 
                srpdSubmission: dryRun ? statusTemplates.srpdValidationSuccess : statusTemplates.srpdSubmissionSuccess,
            }
            setStatusDisplay(statusTracker);
        } catch (e: any) {
            console.error(e);
            const errorMessage = e.message || errorText.unexpectedError;
            statusTracker = {...statusTracker, srpdSubmission:
                (
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                        <div>{dryRun ? statusTemplates.srpdValidationFail : statusTemplates.srpdSubmissionFail}</div>
                        <div style={{color: palette.stepper.error, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{errorMessage}</div>
                    </div>
                )
            };
            setStatusDisplay(statusTracker);
            setIsProcessingSubmit(false);
            setIsSubmitFailure(!dryRun);
            return;
        }

        if (dryRun) {
            setIsProcessingSubmit(false);
            return;
        }

        const jiraPath = `/browse/${issueKey}`;
        const issueURL = "https://" + getJiraAPIBaseHost("production") + jiraPath;
        setIssueLink(issueURL);
        
        const filePath = JFROG_API_SRP_JSON_REPO_URL + "/" + fileName + ".json";
        setPathToPublishedSRPd(filePath);
        setIsSubmitSuccess(true);
        setIsProcessingSubmit(false);

    } catch(e) {
        console.error(e);
        setIsProcessingSubmit(false);
        setIsSubmitFailure(!dryRun);
        setSubmitStatus(errorText.unexpectedError);
    }
};
    // Displays the schema validation success box
    function validationSuccessful(){
        // If schema isn't corrently fetched, don't display success message
        if (schema && !schema["$schema"]){return;}

        const successfulValidationAJV: React.CSSProperties = {
            overflowY: "scroll",
            width: "99%",
            borderRadius: "5px",
            border: "3px solid #5c914a",
            color: "black",
            backgroundColor: "#d4ffe9",
            whiteSpace: "pre",
            position: "relative",
            padding: "10px 10px 10px 10px",
            margin: "5px 5px 5px 5px",
            fontSize: "12pt"
        }

        setSchemaErrorReadout([]);
    }


    async function handleSchemaValidationButton(e: any){
        e?.preventDefault();

        const data = await jsonFunction();

        if (handleJsonFunctionError(data.error, 'validate')) {
            return;
        }

        if(!schema){
            setSubmitStatus(errorText.fetchingSchema);
            return;
        }

        setSubmitStatus("");

        const {srpContent, _} = data;

        const isValid = validateFormAndSchema({
            srpContent,
        });

        if (!isValid) {
            return;
        }

        validationSuccessful();
    }

    return(
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '20vh',
            justifyContent: "space-evenly",
            marginBottom: '30px'
        }}>
            {/* warning display for when the schema fetch fails */}
            {(schema && !schema["$schema"]) && <div id={'SchemaFetchFailed'} style={{color:palette.stepper.error, textAlign:'center'}}> WARNING: Error occurred when fetching schema. Problems in SRP may not be caught when submitting.</div>}
            
            {/* Container that evenly spreads out the validate and submit buttons  */}
            <div style={{ display: 'flex', justifyContent: "space-evenly", alignItems: 'center', margin: '25px 0', width: '100%'}}>
                <StyledValidateButton 
                    type="button" 
                    onClick={(e) => {
                        handleComplete();
                        handleSchemaValidationButton(e);
                    }}
                    >
                    {schema ? "Validate Schema" : "Fetching Schema..." }
                </StyledValidateButton>

                {schemaErrorReadout && schemaErrorReadout.length === 0 && isFormValid && (
                    <SubmitButton
                        onClick={handleConfirmation}
                        buttonText={isSubmitSuccess ? 'SRP Submitted' : dryRun ? 'Check Readiness' : 'Submit SRP'}
                        tooltipTitle={dryRun && !isSubmitSuccess ? READINESS_CHECK_TOOLTIP : ''}
                        isFormSaved={isSubmitSuccess}
                        isLoading={isProcessingSubmit}
                        isFailedSubmit={isSubmitFailure}
                    />
                )}
            </div>      

            {/* Display schema errors if present */}
            { schemaErrorReadout && schemaErrorReadout.length > 0 && 
                <div style={{margin: "10px 0 20px 20px"}}>
                    <div style={{color: palette.stepper.error, fontWeight: 'bold'}}>Schema Validation Errors:</div>  
                    {schemaErrorReadout.map((error, index) => (
                        <React.Fragment key={`schemaErrorReadout_tab_${JSON.stringify(error)}`}>{error}</React.Fragment>
                    ))}
                </div>
            }

            { schemaErrorReadout && schemaErrorReadout.length === 0 && isFormValid &&
                <div style={{
                    margin: '10px 0 20px 20px',
                    color: palette.stepper.main,
                    fontWeight: 'bold'
                }}>
                    Schema Validation Successful
                </div>
            }


            {/* Submit status readout */}
            {submitStatus !== "" && <div>{submitStatus}</div>}
            {Object.keys(statusDisplay).map((key) => statusDisplay[key] && <div key={key}>{statusDisplay[key]}</div>)}


            {/* Links, will display when submitting is finished */}
            {!isProcessingSubmit && issueLink &&
                (<div style={{marginTop:'15px'}}>Link to created Jira Request 
                <div>
                    <a style={{cursor:'pointer'}} 
                        href={`${sanitizeUrl(issueLink)}`}>
                        {`${sanitizeUrl(issueLink)}`}
                    </a>
                </div>
            </div>)
            }

            {!isProcessingSubmit && pathToPublishedSRPd &&
                (<div style={{marginTop:'15px', marginBottom: '30px'}}>
                    Link to published SRPd
                    <div>
                        <a style={{cursor:'pointer'}} 
                            href={pathToPublishedSRPd} 
                            target="_blank" 
                            rel="noopener noreferrer">
                            {pathToPublishedSRPd} 
                        </a>
                    </div>
                </div>)
            }

            <SubmitModal 
                dryRun={dryRun}
        
                isProcessingSubmit={isProcessingSubmit}
                
                setIsProcessingSubmit={setIsProcessingSubmit}

                handleSubmit={handleSubmit}
                
                submitStatus={submitStatus} 
                
                statusDisplay={statusDisplay} 

                modalType={modalType}

                setModalType={setModalType}
            />
                
        </div>
    );
}

export default SubmitSection;